import bbox from '@turf/bbox';
import buffer from '@turf/buffer';
import center from '@turf/center';
import distance from '@turf/distance';
import { polygon, Units } from '@turf/helpers';

// This is needed to excluding GL JS explicitly from transpilation
// See https://docs.mapbox.com/mapbox-gl-js/guides/install/
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl';

import { GeoFilter, GeoFilterType } from '../../contexts/FilterContext';
import { PersonFilterInput } from '../../types/api.graphql';
import { hashCode } from '../helpers';
import { DrawProps, GeoFilterHelper, MarkerProps } from './GeoFilterHelper';

export interface GeoFilterPolygonData {
  polygonCoordinates?: { longitude: number; latitude: number }[];
}

export default class PolygonGeoFilterHelper extends GeoFilterHelper<GeoFilterPolygonData> {
  generateId(): number {
    return hashCode(
      `${this.geoFilter.data.polygonCoordinates?.[0]?.latitude}, ${
        this.geoFilter.data.polygonCoordinates?.[0]?.longitude
      }, ${(this.geoFilter.isParent || false).toString()}`,
    );
  }

  isEqual(geoFilter: GeoFilter): boolean {
    if (!super.isEqual(geoFilter)) return false;

    if (geoFilter.data.polygonCoordinates.length !== this.geoFilter.data.polygonCoordinates.length) return false;
    for (let i = 0; i < geoFilter.data.polygonCoordinates.length; i++) {
      if (
        geoFilter.data.polygonCoordinates[i].latitude !== this.geoFilter.data.polygonCoordinates[i].latitude ||
        geoFilter.data.polygonCoordinates[i].longitude !== this.geoFilter.data.polygonCoordinates[i].longitude
      )
        return false;
    }
    return true;
  }

  toGraphQLGeoFilter(skipExcludedZips?: boolean): PersonFilterInput {
    return {
      geoFilter: {
        operator: {
          polygon: this.geoFilter.data.polygonCoordinates?.map((coordinate) => ({
            latitude: coordinate.latitude,
            longitude: coordinate.longitude,
          })),
        },
        negate: this.geoFilter.negate || false,
        ...(!skipExcludedZips && { excludedZipCodes: this.geoFilter.excludedZipCodes }),
      },
    };
  }

  generateIdForGeoFilterGraphQl(): number {
    return hashCode(
      `${this.geoFilterGraphQL.operator.polygon[0]?.latitude}, ${this.geoFilterGraphQL.operator.polygon[0]?.longitude}`,
    );
  }

  fromGraphQLGeoFilter(): GeoFilter<GeoFilterPolygonData> {
    const data: GeoFilterPolygonData = {
      polygonCoordinates: this.geoFilterGraphQL.operator.polygon,
    };
    return {
      id: this.generateIdForGeoFilterGraphQl(),
      type: GeoFilterType.Polygon,
      data,
      negate: this.geoFilterGraphQL.negate || false,
      excludedZipCodes: this.geoFilterGraphQL.excludedZipCodes,
    };
  }

  private generateCenterOfPolygon = () => {
    const polygonCoordinates = this.geoFilter?.data.polygonCoordinates?.map((coord) => {
      return [coord.longitude, coord.latitude];
    });

    const polygonRet = polygon([polygonCoordinates]);
    return center(polygonRet);
  };

  getCenter() {
    const center = this.generateCenterOfPolygon();
    return { latitude: center.geometry.coordinates[1], longitude: center.geometry.coordinates[0] };
  }

  getBoundingBox() {
    const polygonCoordinates = this.geoFilter.data.polygonCoordinates?.map((coord) => {
      return [coord.longitude, coord.latitude];
    });

    const center = this.generateCenterOfPolygon();
    let furthestPointFromCenter = polygonCoordinates[0];
    const distanceMax = distance(center, furthestPointFromCenter, { units: 'miles' });
    polygonCoordinates.forEach((coord) => {
      const distanceRet = distance(center, coord, { units: 'miles' });
      if (distanceRet > distanceMax) furthestPointFromCenter = coord;
    });

    const distanceRet = distance(center, furthestPointFromCenter, { units: 'miles' });
    const options = { units: 'miles' as Units };
    const buffered = buffer(center, distanceRet, options);
    return bbox(buffered);
  }

  drawLayer(props: DrawProps) {
    const coordinates = this.geoFilter.data.polygonCoordinates.map((coordinate) => [
      coordinate.longitude,
      coordinate.latitude,
    ]);

    const polygonData = {
      type: 'Feature',
      id: this.geoFilter.id,
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
    };
    props.drawer.add(polygonData);

    // Add custom map layers for visual styling (color support for negated filters)
    const sourceId = `polygon-${this.geoFilter.id}`;
    props.map.addSource(sourceId, {
      type: 'geojson',
      data: polygonData as GeoJSON.Feature,
    });

    const layerFillId = `polygon-fill-${this.geoFilter.id}`;
    props.map.addLayer({
      id: layerFillId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': props.color,
        'fill-opacity': 0.1,
      },
    });

    const layerStrokeId = `polygon-stroke-${this.geoFilter.id}`;
    props.map.addLayer({
      id: layerStrokeId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': props.color,
        'line-width': 1.5,
        'line-dasharray': [3, 3],
      },
    });

    // Don't return layerFillId — polygon has no center marker, so no hover popup
    return {};
  }

  drawMarker(props: MarkerProps) {
    const el = document.createElement('div');
    el.style.display = 'none';
    return new mapboxgl.Marker(el).setLngLat(props.position).addTo(props.map);
  }
}
